import CommerceLogisticsCustoms, { emptyCustoms } from './CommerceLogisticsCustoms'
import { useEffect, useRef, useState } from 'react'
import { fetchCommerceData } from '../api'
import { requestCommerceManagement } from '../management-api.js'
import { errorMessage, statusLabel, TRACKING_STATUS_LABELS } from './CommerceLogistics.helpers'
import { canFulfill, configuredLogisticsProviders, remainingItems, createLogisticsOperations, fulfillmentBody, manualPackages, providerPackages, manualTrackingBody, MANUAL_STATUSES, quoteBody } from './CommerceLogistics.actions.js'

const inputClass = 'mt-1 block w-full rounded border border-slate-300 bg-white p-2 dark:border-slate-700 dark:bg-slate-900'
const buttonClass = 'rounded border border-slate-300 px-3 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700'
const emptySender = { name: '', company: '', street1: '', street2: '', city: '', state: '', zip: '', country: '', phone: '', email: '' }
const emptyParcel = { length: '', width: '', height: '', distanceUnit: 'cm', weight: '', massUnit: 'kg' }

function Field({ label, ...props }) {
  return <label className="block text-sm">{label}<input className={inputClass} {...props} /></label>
}

export default function CommerceLogisticsActions({ aggregate, lang, operations: suppliedOperations, onSuccess, onBusyChange }) {
  const order = aggregate.order
  const unshippedOrder = { ...order, items: remainingItems(aggregate) }
  const t = (zh, en) => lang === 'zh' ? zh : en
  const operations = useRef(null)
  if (!operations.current) operations.current = suppliedOperations || createLogisticsOperations()
  const lock = useRef(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [providers, setProviders] = useState([])
  const [providerError, setProviderError] = useState('')
  const [providerAttempt, setProviderAttempt] = useState(0)
  const [provider, setProvider] = useState('')
  const [mode, setMode] = useState('tracking')
  const [quantities, setQuantities] = useState({})
  const [carrier, setCarrier] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [sender, setSender] = useState(emptySender)
  const [parcel, setParcel] = useState(emptyParcel)
  const [customs, setCustoms] = useState(emptyCustoms)
  const [quote, setQuote] = useState(null)
  const [rateId, setRateId] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [packageId, setPackageId] = useState('')
  const [providerPackageId, setProviderPackageId] = useState('')
  const [status, setStatus] = useState('in_transit')
  const [description, setDescription] = useState('')
  const packages = manualPackages(aggregate)
  const refreshablePackages = providerPackages(aggregate, providers)

  useEffect(() => {
    const controller = new AbortController()
    setProviderError('')
    fetchCommerceData('health', { signal: controller.signal }).then((health) => {
      if (controller.signal.aborted) return
      const configured = configuredLogisticsProviders(health)
      setProviders(configured)
      setProvider((current) => configured.includes(current) ? current : configured.includes('manual') ? 'manual' : configured[0] || '')
    }).catch((cause) => { if (!controller.signal.aborted) setProviderError(errorMessage(cause, lang)) })
    return () => controller.abort()
  }, [lang, providerAttempt])

  function invalidateQuote() { setQuote(null); setRateId(''); setConfirmed(false) }

  async function perform(action) {
    if (lock.current) return
    lock.current = true
    setBusy(true)
    onBusyChange(true)
    setError('')
    setNotice('')
    try { await action() } catch (cause) { setError(errorMessage(cause, lang)) }
    finally { lock.current = false; setBusy(false); onBusyChange(false) }
  }

  async function refreshAfterWrite() {
    setNotice(t('操作已保存。', 'Operation saved.'))
    try { await onSuccess() }
    catch (cause) { setError(`${t('已保存，但刷新失败，请刷新页面，勿重复提交。', 'Saved, but refresh failed. Refresh the page; do not resubmit.')} ${errorMessage(cause, lang)}`) }
  }

  function getQuote(event) {
    event.preventDefault()
    perform(async () => {
      if (provider !== 'shippo' || !providers.includes(provider)) throw new Error(t('Shippo 未配置。', 'Shippo is not configured.'))
      const body = quoteBody(sender, parcel, { shippingAddress: order.shippingAddressSnapshot, customs })
      const result = await operations.current.run(requestCommerceManagement, `orders/${encodeURIComponent(order.id)}/fulfillment-quotes`, body)
      setQuote(result)
      setRateId('')
      setConfirmed(false)
      if (!result?.rates?.length) setNotice(t('暂无可用费率，请检查地址或重新获取报价。', 'No rates available. Check the address or request another quote.'))
    })
  }

  function create(event) {
    event.preventDefault()
    perform(async () => {
      if (!providers.includes(provider)) throw new Error(t('请选择已配置服务商。', 'Select a configured provider.'))
      const body = fulfillmentBody({ order: unshippedOrder, quantities, provider, mode, carrier, trackingNumber, quote, rateId, confirmed })
      await operations.current.run(requestCommerceManagement, `orders/${encodeURIComponent(order.id)}/fulfillments`, body)
      invalidateQuote()
      setQuantities({})
      setTrackingNumber('')
      await refreshAfterWrite()
    })
  }

  function updateTracking(event) {
    event.preventDefault()
    perform(async () => {
      if (!packages.some((item) => item.id === packageId)) throw new Error(t('请选择手工物流包裹。', 'Select a manual shipment package.'))
      await operations.current.run(requestCommerceManagement, `packages/${encodeURIComponent(packageId)}/tracking`, { status, description }, () => manualTrackingBody(status, description))
      setDescription('')
      setPackageId('')
      await refreshAfterWrite()
    })
  }

  function refreshTracking(event) {
    event.preventDefault()
    perform(async () => {
      const parcel = refreshablePackages.find(item => item.id === providerPackageId)
      if (!parcel) throw new Error(t('请选择已配置服务商的包裹。', 'Select a package with a configured provider.'))
      await operations.current.run(requestCommerceManagement, `packages/${encodeURIComponent(parcel.id)}/tracking`, { provider: parcel.provider })
      await refreshAfterWrite()
    })
  }

  return <section className="space-y-4 border border-slate-200 p-5 dark:border-slate-800" aria-busy={busy}>
    <h3 className="font-bold">{t('履约与手工物流操作', 'Fulfillment and manual tracking actions')}</h3>
    <p className="text-xs text-slate-500">{t('如请求超时，先检查订单结果；重试请保持原表单不变，以复用同一操作键。不要重新购买面单。', 'If a request times out, check the order first. Retry unchanged inputs to reuse the same operation key; do not start another label purchase.')}</p>
    {error && <p role="alert" className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}
    {notice && <p role="status" className="text-sm">{notice}</p>}
    {providerError && <p role="alert" className="text-sm">{providerError} <button type="button" disabled={busy} className="underline" onClick={() => setProviderAttempt((value) => value + 1)}>{t('重试服务商配置', 'Retry provider configuration')}</button></p>}
    {canFulfill(order) && unshippedOrder.items.some(item => item.quantity > 0) && <>
      {!providers.length && <p className="text-sm">{t('没有可用的已配置服务商；创建履约与报价已禁用。', 'No configured providers available; fulfillment and quotes are disabled.')}</p>}
      <fieldset disabled={busy || !providers.length} className="space-y-3 disabled:opacity-60">
        <label className="block text-sm">{t('物流服务商', 'Logistics provider')}<select className={inputClass} value={provider} onChange={(event) => { setProvider(event.target.value); setMode('tracking'); invalidateQuote() }}>
          <option value="">{t('请选择', 'Select')}</option>{providers.map((name) => <option key={name} value={name}>{name}</option>)}
        </select></label>
        {provider === 'shippo' && <label className="block text-sm">{t('履约方式', 'Fulfillment mode')}<select className={inputClass} value={mode} onChange={(event) => { setMode(event.target.value); invalidateQuote() }}><option value="tracking">{t('已有运单', 'Existing tracking number')}</option><option value="label">{t('购买面单（收费）', 'Purchase label (chargeable)')}</option></select></label>}
        {mode === 'label' && <form className="space-y-3" onSubmit={getQuote}>
          <h4 className="font-semibold">{t('发件地址（收件地址使用订单地址）', 'Sender address (recipient address comes from the order)')}</h4>
          <div className="grid gap-3 sm:grid-cols-2">{[['name', '姓名'], ['company', '公司'], ['street1', '地址'], ['street2', '地址第二行'], ['city', '城市'], ['state', '州/省'], ['zip', '邮编'], ['country', '国家代码'], ['phone', '电话'], ['email', '邮箱']].map(([key, label]) => <Field key={key} label={t(label, key)} required={['name', 'street1', 'city', 'zip', 'country'].includes(key)} maxLength={key === 'country' ? 2 : undefined} value={sender[key]} onChange={(event) => { setSender({ ...sender, [key]: key === 'country' ? event.target.value.toUpperCase() : event.target.value }); invalidateQuote() }} />)}</div>
          <h4 className="font-semibold">{t('包裹（厘米 / 千克）', 'Parcel (cm / kg)')}</h4>
          <div className="grid grid-cols-2 gap-3">{[['length', '长'], ['width', '宽'], ['height', '高'], ['weight', '重量']].map(([key, label]) => <Field key={key} label={t(label, key)} required type="number" min="0.01" step="0.01" value={parcel[key]} onChange={(event) => { setParcel({ ...parcel, [key]: event.target.value }); invalidateQuote() }} />)}</div>
          <CommerceLogisticsCustoms sender={sender} shippingAddress={order.shippingAddressSnapshot} value={customs} lang={lang} onChange={(value) => { setCustoms(value); invalidateQuote() }} />
          <button className={buttonClass} type="submit" disabled={busy}>{t('获取 Shippo 报价', 'Get Shippo quote')}</button>
        </form>}
        <form onSubmit={create} className="space-y-3">
          <h4 className="font-semibold">{t('本次发货数量', 'Quantities for this shipment')}</h4>
          {unshippedOrder.items.map((item) => <Field key={item.id} label={`${item.name || item.sku || item.id} (${t('待发货', 'remaining')}: ${item.quantity})`} type="number" min="0" max={item.quantity} step="1" value={quantities[item.id] || ''} onChange={(event) => { setQuantities({ ...quantities, [item.id]: event.target.value }); setConfirmed(false) }} />)}
          {provider === 'test-ship' ? <p>{t('仅测试物流；服务端生成测试运单，不购买真实面单。', 'Test logistics only; the server generates test tracking. No real label is purchased.')}</p> : mode === 'label' ? <>
            <label className="block text-sm">{t('费率', 'Rate')}<select required className={inputClass} value={rateId} onChange={(event) => { setRateId(event.target.value); setConfirmed(false) }}><option value="">{t('请选择报价', 'Select a quoted rate')}</option>{(quote?.rates || []).map((rate) => <option key={rate.id} value={rate.id}>{rate.provider} · {rate.serviceLevel?.name || rate.serviceLevel?.token} · {rate.amount} {rate.currency}</option>)}</select></label>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" required checked={confirmed} disabled={!rateId} onChange={(event) => setConfirmed(event.target.checked)} /><span>{t('我确认按以上所选费率购买面单，此操作会产生实际费用。', 'I confirm purchasing the label at the selected rate above. This incurs a real charge.')}</span></label>
          </> : <>
            <Field label={t('承运商（17TRACK 可选）', 'Carrier (17TRACK: numeric ID, optional)')} required={provider !== '17track'} value={carrier} onChange={(event) => setCarrier(event.target.value)} />
            <Field label={t('已有物流单号', 'Existing tracking number')} required value={trackingNumber} onChange={(event) => setTrackingNumber(event.target.value)} />
          </>}
          <button type="submit" className={buttonClass} disabled={busy || !(order.items || []).length || (mode === 'label' && (!confirmed || !rateId))}>{busy ? t('处理中…', 'Working…') : mode === 'label' ? t('确认购买面单并创建履约', 'Purchase label and create fulfillment') : t('创建履约', 'Create fulfillment')}</button>
        </form>
      </fieldset>
    </>}
    {refreshablePackages.length > 0 && <form className="space-y-3 border-t pt-4" onSubmit={refreshTracking}>
      <h4 className="font-semibold">{t('从物流服务商刷新', 'Refresh from logistics provider')}</h4>
      <fieldset disabled={busy} className="space-y-3 disabled:opacity-60">
        <label className="block text-sm">{t('服务商包裹', 'Provider package')}<select className={inputClass} required value={providerPackageId} onChange={(event) => setProviderPackageId(event.target.value)}><option value="">{t('请选择包裹', 'Select a package')}</option>{refreshablePackages.map((item) => <option key={item.id} value={item.id}>{item.provider} · {item.trackingNumber || item.id}</option>)}</select></label>
        <p className="text-xs text-slate-500">{t('查询已配置服务商并保存最新物流状态，不购买面单。', 'Queries the configured provider and saves tracking updates. Does not purchase a label.')}</p>
        <button type="submit" disabled={busy || !providerPackageId} className={buttonClass}>{t('刷新并保存物流', 'Refresh and save tracking')}</button>
      </fieldset>
    </form>}
    {packages.length > 0 && <form className="space-y-3 border-t pt-4" onSubmit={updateTracking}>
      <h4 className="font-semibold">{t('更新手工物流（不调用第三方）', 'Update manual tracking (no provider call)')}</h4>
      <fieldset disabled={busy} className="space-y-3 disabled:opacity-60">
        <label className="block text-sm">{t('包裹', 'Package')}<select className={inputClass} required value={packageId} onChange={(event) => setPackageId(event.target.value)}><option value="">{t('请选择包裹', 'Select a package')}</option>{packages.map((item) => <option key={item.id} value={item.id}>{item.trackingNumber || item.id}</option>)}</select></label>
        <label className="block text-sm">{t('状态', 'Status')}<select className={inputClass} value={status} onChange={(event) => setStatus(event.target.value)}>{MANUAL_STATUSES.map((value) => <option key={value} value={value}>{statusLabel(value, TRACKING_STATUS_LABELS, lang)}</option>)}</select></label>
        <Field label={t('节点说明', 'Event description')} value={description} onChange={(event) => setDescription(event.target.value)} />
        <button type="submit" disabled={busy || !packageId} className={buttonClass}>{t('保存物流状态', 'Save tracking status')}</button>
      </fieldset>
    </form>}
  </section>
}
